import { Injectable } from '@angular/core';
import { Headers, RequestOptions } from '@angular/http';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import * as objectPath from 'object-path';
import { Property, PropertyCollection, Uccancellation, Ucpatch, UcSearchPost } from 'ri-api-client';
import { Observable, Subject } from 'rxjs/Rx';
import { forkJoin } from 'rxjs/observable/forkJoin';
import { EmptyObservable } from 'rxjs/observable/EmptyObservable'
import { combineLatest } from 'rxjs/observable/combineLatest';
import { defaultIfEmpty, take } from 'rxjs/operators';
import { ApiService } from '../api.service';
import { Pattern } from './pattern.service';
import { SettingService } from './setting.service';
import { ParametersService } from './parameters.service';
import { ApiQueryParams } from './api-query-params';
import { IService } from '../interfaces/iservice';
import { Helper } from '../helpers/helper';
import { ChannelService, IUcChannelData } from './channel.service';
import { FilterService } from './filter.service';
import { BillingMeasurementNoteService, IBillingMeasurementNote } from './billing-measurement-note.service';
import { LanguageService } from '../language.service';
import { NotificationService } from '../api/notification.service';
import * as popsicle from 'popsicle';
import * as Highcharts from 'highcharts';
import { SelectionService } from './selection.service';
import { MeterNeighborDto } from '../interfaces/iuc';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ConfigService } from '../../config.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { of } from 'rxjs/observable/of';
import { Ri5UcsService, UcExternalProperties } from '../ri5-api';

export interface Endpoint {
  checked?: boolean;
  id: string;
  consumerId: string;
  coordinateX: number;
  coordinateY: number;
  quantityToIncrease: number;
  quantityToRecover: number;
  ucClass: { 'name': string };
  ucClassification: { 'id': number, 'name': string };
  region: { 'name': string };
  locality: { 'name': string };
  municipality: { 'name': string };
  neighborhood: { 'name': string };
  calculated?: { hasSmartMeter?: boolean; patternRiskMaxSum?: number };
  patterns: Pattern[];
  selectionPatterns: Pattern[];
}

interface IResult {
  uc: any;
  externalProperties?: any;
  channels: any[];
  ucChannels: IUcChannelData[];
  billingMeasurementNotes: IBillingMeasurementNote[];
  chartOptions: {};
}

@Injectable()
export class UcService implements IService {
  private options: RequestOptions;
  public translations = {
    ENERGY_TO_RECOVER: '',
    ENERGY_TO_INCREMENT: '',
    RESIDENTIAL: '',
    COMMERCIAL: '',
    VERY_HIGH: '',
    HIGH: '',
    AVERAGE: '',
    LOW: '',
    VERY_LOW: '',
    RISK: '',
    CONSUMPTION: '',
    CONSUMPTION_MEASURED: '',
    LOGICAL_NEIGHBOUR: '',
    NOTE: '',
    ATR_Fraud: '',
    ATR_Regular: '',
    ATR_Irregular: '',
    ATR_NotInspected: '',
    SERVICE_ORDER_TYPE: '',
    ACTION_TYPE_RESULT: '',
    NOTE_GROUP: '',
    MEASUREMENT_NOTE: '',
    CLASS: '',
    ENERGY_CHART_Y_AXIS_TITLE: '',
    SO_TECHNICIAN_1: ''
  };

  private consumerProperties: PropertyCollection.PropertyCollection;
  private monthsToConsider = 60;
  private startWithJanuary = true;
  private tasks = [];
  private channels = [];

  constructor(
    public languageSvc: LanguageService,
    private translate: TranslateService,
    private apiService: ApiService,
    private settingService: SettingService,
    private helper: Helper,
    private paramService: ParametersService,
    private channelService: ChannelService,
    private filterService: FilterService,
    private billingMeasurementNoteService: BillingMeasurementNoteService,
    private selectionService: SelectionService,
    private notificationService: NotificationService,
    private http: HttpClient,
    private oAuth: OAuthService,
    private languageService: LanguageService,
    private ri5UcsService: Ri5UcsService
  ) {
    this.setMommentLanguage(this.languageService.actualLanguage);
    this.languageService.actualLanguage$.subscribe((language) => {
      this.setMommentLanguage(language);
    });
    const headers = new Headers({ 'Content-Type': 'application/json' });
    this.options = new RequestOptions({ headers: headers });
    this.apiService.init();
    this.paramService.getParameters('CONSUMPTIONCHART').subscribe((response) => {
      // Beware of case sensitivity here....
      this.monthsToConsider = objectPath.get(response, 'CONSUMPTIONCHART.MonthsToConsider', this.monthsToConsider);
      this.startWithJanuary = objectPath.get(response, 'CONSUMPTIONCHART.StartWithJanuary', this.startWithJanuary);
    });
    this.translate.get(['ATR_Efetivo', 'ATR_Parc_Efetivo', 'ATR_Nao_Efetivo', 'NOTE']).subscribe((translations: string) => {
      this.translations['ATR_Efetivo'] = translations['ATR_Efetivo'];
      this.translations['ATR_Parc_Efetivo'] = translations['ATR_Parc_Efetivo'];
      this.translations['ATR_Nao_Efetivo'] = translations['ATR_Nao_Efetivo'];
      this.translations['NOTE'] = translations['NOTE'];
    });
    this.createCustomMarkers();

    this.apiService.client.ucs.options.GET('Consumer Property').subscribe(response => {
      this.consumerProperties = response._200;
      this.consumerProperties.results.filter(p => p.isExtended).forEach(property => {
        this.tasks.push(this.filterService.getUcPropertyValues(property.id));
      });
    });

    this.channelService.getChannels().subscribe(channels => {
      this.channels = channels;
    });
  }
  private setMommentLanguage(actualLanguage: any) {
    switch (actualLanguage) {
      case 'en':
        moment.locale('en-US');
        break;
      case 'es':
        moment.locale('es-ES');
        break;
      default:
        moment.locale('pt-BR');
    }
  }

  jsonDateToMonthYearFormatted(date: string): string {
    if (date) {
      return `${date.substring(5, 7)}/${date.substring(0, 4)}`;
    }
    return null;
  }

  dateToMonthYearFormatted(date: Date): string {
    if (date) {
      return this.jsonDateToMonthYearFormatted(new Date(date.getTime() - (date.getTimezoneOffset() * 60000)).toJSON());
    }
    return null;
  }

  getAll(query: ApiQueryParams): Observable<any> {
    return this.getUCs(query);
  }
  getUCs(query: ApiQueryParams): Observable<any> {
    // If request url exceeds the 2k limit use post/get by id instead of get
    const size = (query.fields || '').length +
      (query.filter || '').length +
      (query.group || '').length +
      (query.aggregatedFields || '').length +
      (query.page || []).length +
      (query.sort || '').length +
      (query.patternsFilter || '').length +
      (query.selectionsFilter || '').length;

    // Leave a reasonable error margin due to extra encoding chars and base url
    if (size > 1000) {
      return this.apiService.client.ucs.search.POST(query, this.options)
        .map((res) => {
          return this.apiService.handleResponse(res);
        });
    }

    return this.apiService.client.ucs.search.GET(query.patternsFilter, query.selectionsFilter, query.fields, query.filter, query.group, query.aggregatedFields, query.page, query.skip, query.take, query.sort, this.options)
      .map((res) => {
        return this.apiService.handleResponse(res);
      });
  }

  getUCsWithCount(query: ApiQueryParams): Observable<any> {
    const dataQuery = this.getUCs(query);
    const countQuery = this.getUCsCount(query.filter);
    return forkJoin([dataQuery, countQuery]);
  }
  getCount(filter: string): Observable<any> {
    return this.getUCsCount(filter);
  }
  getUCsCount(filter: string): Observable<any> {
    return this.getUCs({
      filter: filter,
      aggregatedFields: 'Count()'
    });
  }

  getUCsPendingInspectionRequests(query: ApiQueryParams): Observable<any> {
    if (query.filter && query.filter.trim()) {
      query.filter = `Calculated.HasPendingSelection AND (${query.filter})`;
    } else {
      query.filter = 'Calculated.HasPendingSelection';
    }
    const dataQuery = this.getUCs(query);
    const countQuery = this.getUCsCount(query.filter);
    return forkJoin([dataQuery, countQuery]);
  }

  getUC(id: string, fields: string): Observable<any> {
    return this.apiService.client.ucs.ucId(id).GET(fields, this.options)
      .map((res) => {
        return this.apiService.handleResponse(res);
      });
  }

  getProperties(id: string, contexts: string = 'Consumer Property', includeServiceOrders?: boolean, includePatterns?: boolean, includeSelections?: boolean): Observable<any> {

    let fields = this.consumerProperties.results.map(x => x.modelName).join(',');

    if (includeServiceOrders) {
      fields += `, ServiceOrders.DateExecuted, ServiceOrders.DateCreated, ServiceOrders.DateCancelled,
          ServiceOrders.soTechnician1.Name, ServiceOrders.commentNote,
          ServiceOrders.SoType.Id, ServiceOrders.SoResult.Id, ServiceOrders.SoResult.ActionTypeResult.Id,
          ServiceOrders.SoType.Name, ServiceOrders.SoType.Symbol, ServiceOrders.SoType.Color,
          ServiceOrders.SoResult.Name, ServiceOrders.SoResult.ActionTypeResult.ResourceKey,
          ServiceOrders.SoResult.ActionTypeResult.Symbol, ServiceOrders.SoResult.ActionTypeResult.Color`;
    }
    if (includePatterns) {
      fields += ', Patterns.Pattern.Name, Patterns.Pattern.App.Name, Patterns.Risk, Patterns.Pattern.DateDeleted';
    }
    if (includeSelections) {
      fields += ', selections.idSelection, selections.patterns.originalRisk, selections.patterns.risk, selections.patterns.pattern.name, selections.patterns.pattern.app.name';
    }

    const props = new Subject();
    this.apiService.client.ucs.ucId(id).GET(fields, this.options).map(data => data._200).subscribe(result => {

      // Gets descriptions in UcPropertyValues, for the extended properties
      // Note: All UC_COL_ID_xx values are kept in a separate table, UC_PROPERTY_VALUES, and are tagged with is_extended = 1. We cannot grab their value description directly from UCs table.
      const tasks = [];
      this.consumerProperties.results.filter(p => p.isExtended).forEach(property => {
        tasks.push(this.filterService.getUcPropertyValues(property.id));
      });

      // Fail-safe condition - defaultIfEmpty - in case we don't have ANY extended property.
      Observable.forkJoin(...tasks).pipe(defaultIfEmpty([])).subscribe(propValues => {

        // Due to forkJoin nature, propertyValues come in a subarray per each property, so we need to flat them.
        // Because flat or flatMap functions are not available in ES2016, we have to use code below, taken from https://stackoverflow.com/questions/10865025/merge-flatten-an-array-of-arrays
        const flattenPropValues = [].concat.apply([], propValues);

        const ucProperties =
          this.consumerProperties.results.map(x => {
            return {
              id: this.helper.toCamelCase(x.modelName),
              displayName: this.translate.instant(x.displayName),
              value: this.resolveUcPropertyDescription(x, flattenPropValues, result),
              propertyId: x.id,
              order: x.atUCDetails,
              isExtended: x.isExtended
            };
          });

        props.next({ ucProperties: ucProperties, serviceOrders: result.serviceOrders, patterns: result.patterns });
      });

    });

    return props.asObservable();
  }

  getExternalProperties(id: string): Observable<UcExternalProperties> {
    return this.ri5UcsService.getExternalProperties(id);
  }

  private resolveUcPropertyDescription(propertyDefinition: Property.Property, propertyValuesCollection, ucProperties): any {
    // Resolves description for a UcProperty. When it's extended property, we need to grab description from another source (I know it's weird)
    if (propertyDefinition.isExtended && propertyValuesCollection) {
      const ret = propertyValuesCollection.find(p => p.propertyId === propertyDefinition.id && p.id === objectPath.get(ucProperties, this.helper.toCamelCase(propertyDefinition.modelName)));
      if (ret && ret.hasOwnProperty('description')) { return ret.description; } else { return objectPath.get(ucProperties, this.helper.toCamelCase(propertyDefinition.modelName)); }
    } else if (propertyDefinition.modelName.startsWith('UcColDate')) {
      // Special case when we have dates
      const retDate = objectPath.get(ucProperties, this.helper.toCamelCase(propertyDefinition.modelName));
      if (retDate) {
        const _date = new Date(retDate.toString());
        const _dateOptions = { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' };
        return _date.toLocaleString(this.languageSvc.actualLanguage, _dateOptions);
      } else { return '' }
    } else {
      return objectPath.get(ucProperties, this.helper.toCamelCase(propertyDefinition.modelName));
    }
  }

  getMarkers() {
    // Note the special case below for the Markers. We only bring SoTypes, ActionTypeResults and NoteGroups that have a marker (symbol+color) configured, using 'onlyWithConfiguredMarkers' parameter
    const actionTypeResults$ = this.selectionService.getTypeResults(true);
    const soTypes$ = this.selectionService.getSOTypes(true);
    const noteGroups$ = this.billingMeasurementNoteService.getNoteGroups(true);

    return combineLatest([actionTypeResults$, soTypes$, noteGroups$], (actionTypeResults, soTypes, noteGroups) => {
      return {
        actionTypeResults: actionTypeResults,
        soTypes: soTypes.results,
        noteGroups: noteGroups.results
      }
    });
  }

  getBillings(id: string, fields?: string, since?: Date, until?: Date): Observable<any[]> {
    let filter = `ucId eq \"${id}\"`;
    if (since) {
      filter += ` and dateReference ge \"${since.toISOString()}\"`;
    }
    if (until) {
      filter += ` and dateReference le \"${until.toISOString()}\"`;
    }
    return this.apiService.client.billings.search.GET(fields, filter, null, null, null, 'dateReference', this.options)
      .map((res) => {
        return this.apiService.handleResponse(res).results;
      });
  }

  getInvoices(id: string, fields?): Observable<any[]> {
    return this.apiService.client.invoices.GET(fields, `contractId eq \"${id}\"`, null, null, this.options)
      .map((res) => {
        return this.apiService.handleResponse(res).results;
      });
  }

  getServiceOrders(id: string, fields?: string, since?: Date, until?: Date): Observable<any[]> {
    let filter = `UCId eq \"${id}\"`;
    if (since) {
      filter += ` and dateCreated ge \"${since.toISOString()}\"`;
    }
    if (until) {
      filter += ` and dateCreated le \"${until.toISOString()}\"`;
    }
    return this.apiService.client.serviceOrders.search.GET(fields, filter, null, null, null, 'dateCreated', this.options)
      .map((res) => {
        return this.apiService.handleResponse(res).results;
      });
  }

  getEvents(id: string): Observable<any> {
    return this.apiService.client.ucs.ucId(id).GET('ServiceOrders.SoType.Name,ServiceOrders.DateExecuted,ServiceOrders.DateCreated,ServiceOrders.DateCancelled,ServiceOrders.SoResult.Name')
      .map((res) => {
        return this.apiService.handleResponse(res);
      });
  }

  getPatterns(id: string): Observable<any> {
    return this.apiService.client.ucs.ucId(id).GET('Patterns.Pattern.Name,Patterns.Pattern.App.Name,Patterns.Risk,Patterns.Pattern.DateDeleted')
      .map((res) => {
        return this.apiService.handleResponse(res);
      });
  }

  getSmartMeterSeries(id: string, from: Date | string, to?: Date | string, fields?: string, group?: string, aggregatedFields?: string): Observable<any> {
    const fromParam: string = (typeof from === 'string') ? from : (<Date>from).toISOString().substr(0, 16);
    const toParam: string | undefined = (to) ? ((typeof to === 'string') ? to : (<Date>to).toISOString().substr(0, 16)) : undefined;

    return this.apiService.client.ucs.ucId(id).smartMeter.series.GET(fromParam, toParam, fields, group, aggregatedFields, this.options).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  getSmartMeterEvents(id: string, from: Date | string, to?: Date | string): Observable<any> {
    const fromParam: string = (typeof from === 'string') ? from : (<Date>from).toISOString().substr(0, 16);
    const toParam: string | undefined = (to) ? ((typeof to === 'string') ? to : (<Date>to).toISOString().substr(0, 16)) : undefined;

    return this.apiService.client.ucs.ucId(id).smartMeter.events.GET(fromParam, toParam, null, this.options)
      .map((res) => {
        return this.apiService.handleResponse(res);
      });
  }

  cancelPendingUCs(ucs: Uccancellation.Uccancellation) {
    return this.apiService.client.ucs.cancelPendingInspection.POST(ucs, this.options)
      .map(res => {
        return res.responseCode;
      });
  }

  createCustomMarkers() {
    // Criando o marker X
    Highcharts.Renderer.prototype.symbols.x = (x, y, w, h) => {
      return [
        'M', x, y,
        'L', x + w, y + h,
        'M', x + w, y,
        'L', x, y + h,
        'Z'
      ];
    }
    // Criando o marker CRUZ
    Highcharts.Renderer.prototype.symbols.cross = (x, y, w, h) => {
      return [
        'M', x + w / 2, y,
        'L', x + w / 2, y + h,
        'M', x, y + h / 3,
        'L', x + w, y + h / 3,
        'Z'
      ]
    }
    // Criando o marker BARRA
    Highcharts.Renderer.prototype.symbols.bar = (x, y, w, h) => {
      return [
        'M', x + w / 2, y,
        'L', x + w / 2, y + h,
        'Z'
      ]
    }
    // Criando o marker ESTRELA
    Highcharts.Renderer.prototype.symbols.star = (x, y, w, h) => {
      return [
        'M', x, y + 0.4 * h,
        'L', x + 0.35 * w, y + 0.35 * h,
        'L', x + 0.5 * w, y,
        'L', x + 0.65 * w, y + 0.35 * h,
        'L', x + w, y + 0.4 * h,
        'L', x + 0.75 * w, y + 0.65 * h,
        'L', x + 0.85 * w, y + h,
        'L', x + 0.5 * w, y + 0.8 * h,
        'L', x + w * 0.15, y + h,
        'L', x + 0.25 * w, y + 0.65 * h,
        'Z'
      ];
    }
    // Criando o marker CASA
    Highcharts.Renderer.prototype.symbols.house = (x, y, w, h) => {
      return [
        'M', x + w / 2, y,
        'L', x + w, y + h / 2,
        'L', x + w, y + h,
        'L', x, y + h,
        'L', x, y + h / 2,
        'L', x + w / 2, y,
        'Z'
      ];
    }

    // Criando o marker Quadrado Vazado
    Highcharts.Renderer.prototype.symbols.emptySquare = (x, y, w, h) => {
      return [
        'M', x, y,
        'L', x + w, y,
        'L', x + w, y + h,
        'L', x, y + h,
        'Z'
      ];
    }

    // Criando o marker Cruz Vazado
    Highcharts.Renderer.prototype.symbols.emptyCross = (x, y, w, h) => {
      return [
        'M', x + 0.25 * w, y,
        'L', x + 0.75 * w, y,
        'L', x + 0.75 * w, y + 0.25 * h,
        'L', x + w, y + 0.25 * h,
        'L', x + w, y + 0.75 * h,
        'L', x + w * 0.75, y + 0.75 * h,
        'L', x + w * 0.75, y + h,
        'L', x + w * 0.25, y + h,
        'L', x + w * 0.25, y + h * 0.75,
        'L', x, y + h * 0.75,
        'L', x, y + h * 0.25,
        'L', x + w * 0.25, y + h * 0.25,
        'Z'
      ];
    }

    Highcharts.Renderer.prototype.symbols.verticalBar = (x, y, w, h) => {
      return [
        'M', x + w / 2, y,
        'L', x + w / 2, y + h,
        'Z'
      ];
    }
  }

  getEmptyObservable(): Observable<any> {
    return this.channels as any;
  }

  getUcDetails(ucId, seriesSince?: Date, seriesUntil?: Date, channelsAxisConfigurations?: any[], propertiesContexts: string = 'Consumer Property', includeSelectionsProperties?: boolean): Observable<any> {
    const ucDetails = new Subject();

    const today = new Date();
    const until: Date = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59);
    const since: Date = new Date(today.getFullYear(), this.startWithJanuary ? 0 : until.getMonth() - this.monthsToConsider, 1);

    if (!seriesSince) {
      seriesSince = since;
    }
    if (!seriesUntil) {
      seriesUntil = until;
    }

    const monthYearAxis = Array.from({ length: ((seriesUntil.getFullYear() - seriesSince.getFullYear()) * 12) + seriesUntil.getMonth() - seriesSince.getMonth() + 1 }, (e, i) =>
      this.dateToMonthYearFormatted(new Date(seriesSince.getFullYear() + i / 12, seriesSince.getMonth() + i % 12))
    );

    const chartOptions = {
      chart: {},
      title: { text: '' },
      tooltip: {
        formatter: null
      },
      exporting: {
        filename: ucId,
        buttons: {
          contextButton: {
            menuItems: ['downloadJPEG', 'downloadPDF', 'downloadSVG'],
            // titleKey: 'accessibility'
          }
        },
        enabled: true
      },
      series: [],
      xAxis: {
        categories: monthYearAxis,
        labels: {
          rotation: -90,
          style: { textOverflow: 'none' }
        },
        showFirstLabel: true
      },
      yAxis: [],
      legend: { enabled: true },
      credits: { enabled: false }
    };

    const ucObservable = this.getProperties(ucId, propertiesContexts, true, true, includeSelectionsProperties);
    const channelsObservable = this.getEmptyObservable();
    const ucChannelsObservable = this.channelService.getUcChannels(ucId, seriesSince, seriesUntil);
    const ucExternalPropertiesObservable = this.getExternalProperties(ucId);

    const billingMeasurementNotesObservable = this.billingMeasurementNoteService.getUcBillingMeasurementNotes(ucId,
      ` dateReference, commentNote, MeasurementNote.Name,
        MeasurementNote.NoteGroup.Id, MeasurementNote.NoteGroup.Name, MeasurementNote.NoteGroup.Symbol, MeasurementNote.NoteGroup.Color `
    );

    ucObservable.combineLatest(channelsObservable, ucChannelsObservable, billingMeasurementNotesObservable, ucExternalPropertiesObservable, (v0, v1, v2, v3, v4) => {
      return {
              uc: v0,
              channels: this.channels,
              ucChannels: v2,
              billingMeasurementNotes: v3,
              externalProperties: v4,
              chartOptions: {}
            };
    }).pipe(take(1)).subscribe((result: IResult) => {

      let seriesNo = 0;
      result.channels.sort((a, b) => Number(b.referenceChannel) - Number(a.referenceChannel)).forEach((ch, index) => {

        const channelColor = this.rgbToHex(ch.chartMarkerColor);
        const channelData = result.ucChannels !== null && result.ucChannels !== undefined ? result.ucChannels.find(x => x.channelId === ch.id) : null;
        let consumption: number[] = null;

        if (channelData !== null && channelData !== undefined) {
          consumption = monthYearAxis.map(monthYear => {
            const found = channelData.channelData.find(x => this.jsonDateToMonthYearFormatted(x.referenceDate) === monthYear);
            if (found) {
              if (found.valueField === null || found.valueField === undefined || Number.isNaN(found.valueField) || !Number.isFinite(found.valueField)) {
                return 0;
              }
              return found.valueField;
            }

            return null;
          });

          this.translate.get(ch.name).subscribe((translation: string) => {

            const channelAxisConfiguration = channelsAxisConfigurations != null ? channelsAxisConfigurations.filter(x => x.Id === ch.id) : null;
            const oppositeAxis = channelAxisConfiguration != null && channelAxisConfiguration.length > 0 && channelAxisConfiguration[0].Opposite != null ? channelAxisConfiguration[0].Opposite : false;

            if ((consumption !== undefined && consumption.length > 0 && consumption.some(c => c != null)) || (ch.referenceChannel)) {

              // PRDT-432: We are forcing Y-axis creation on the chart even when we don't have data.
              // But these cases need to be handled differently, so we use this variable.
              const seriesIsNotEmpty: boolean = consumption.some(x => x !== null);

              if (!chartOptions.yAxis.find(yAxis => yAxis.channelUnit === ch.unit && yAxis.opposite === oppositeAxis)) {
                chartOptions.yAxis.push({
                  min: seriesIsNotEmpty ? null : 0,
                  max: seriesIsNotEmpty ? null : 10,
                  categories: seriesIsNotEmpty ? null : ['0'],
                  tickAmount: 10,
                  title: { text: `${ch.unit}`, allowDecimals: ch.unit.indexOf('$') === -1 ? false : true },
                  labels: { format: '{value} ' },
                  visible: ch.secondaryAxis || !result.channels.find(x => x.secondaryAxis === true) ? true : false,
                  opposite: oppositeAxis,
                  ChannelId: ch.id,
                  channelUnit: ch.unit
                });
                seriesNo++;
              }
              if (seriesNo > 0) {
              chartOptions.series.push({
                yAxis: chartOptions.yAxis.findIndex(yAxis => yAxis.channelUnit === ch.unit && yAxis.opposite === oppositeAxis),
                type: 'line',
                color: channelColor,
                connectNulls: false,
                name: translation,
                data: consumption,
                marker: {
                  symbol: ch.chartMarkerType
                },
                referenceChannel: ch.referenceChannel,
                tooltip: {
                  valueDecimals: ch.unit.indexOf('$') === -1 ? 0 : 2,
                  valuePrefix: ch.unit,
                  valueSuffix: ''
                },
              });
            }

              if (ch.referenceChannel && seriesNo > 0) {
                chartOptions.yAxis[seriesNo - 1].referenceChannel = ch.referenceChannel;
              }

              if (seriesIsNotEmpty) {
                let allSeriesmaxValue, currentSerieMaxValue = 0;
                const seriesUsingYAxis = chartOptions.series.filter(s => s.yAxis === seriesNo - 1);
                for (let i = 0; i < seriesUsingYAxis.length; i++) {
                  currentSerieMaxValue = Math.max(...seriesUsingYAxis[i].data);
                  currentSerieMaxValue = !Number.isFinite(currentSerieMaxValue) ? 0 : currentSerieMaxValue;
                  allSeriesmaxValue = allSeriesmaxValue > currentSerieMaxValue || (currentSerieMaxValue === Infinity || currentSerieMaxValue === -Infinity || currentSerieMaxValue === NaN) ? allSeriesmaxValue : currentSerieMaxValue;
                }
                chartOptions.yAxis[seriesNo - 1].max = allSeriesmaxValue;
              }
            }
          })

        } else {
          consumption = null;
        }
      });

      const consumptionByMonth = monthYearAxis.map(monthYear => {

        const consumptionInvoiceData = result.ucChannels.find(x => x.channelData.filter(d => d.referenceChannel === true) != null);
        if (consumptionInvoiceData !== null && consumptionInvoiceData !== undefined) {
          const found = consumptionInvoiceData.channelData.find(x => this.jsonDateToMonthYearFormatted(x.referenceDate) === monthYear);
          if (found) {
            return found.valueField;
          }
          // this.notificationService.showNotification('Erro ao carregar dados de consumo', 'error');
          return 0;
        } else {
          return 0;
        }
      });

      const consumptionInvoice = consumptionByMonth.map(element => {
        if (element === undefined) {
          return 0;
        } else {
          return element;
        }
      });

      const atResultsMarkers = [];
      const soTypesMarkers = [];

      if (result.uc && result.uc.serviceOrders) {
        result.uc.serviceOrders.forEach(so => {
          if (so.soResult && so.soResult.actionTypeResult && !atResultsMarkers.some(e => e.id === so.soResult.actionTypeResult.id)) {
            const atr = so.soResult.actionTypeResult;
            if (atr.symbol !== 'none' && (atr.symbol || atr.fillColor)) {
              atResultsMarkers.push({
                id: atr.id,
                name: this.translations[atr.resourceKey],
                key: atr.resourceKey,
                symbol: objectPath.get(atr, 'symbol', 'square'),
                fillColor: objectPath.get(atr, 'color', '#000000')
              });
            }
          } else if (so.soType && !objectPath.get(so.soType, 'soResult.actionTypeResult') && !soTypesMarkers.some(e => e.id === so.soType.id)) {
            const sot = so.soType;

            if (sot.symbol || sot.fillColor) {
              soTypesMarkers.push({
                id: sot.id,
                name: sot.name,
                key: sot.name,
                symbol: objectPath.get(sot, 'symbol', 'square'),
                fillColor: objectPath.get(sot, 'color', '#000000')
              });
            }
          }
        });

        // Action Type results markers
        const cashCollectionActionTypesResults = ['ATR_Efetivo', 'ATR_Parc_Efetivo', 'ATR_Nao_Efetivo'];
        atResultsMarkers.forEach(x => {
          const tooltipData = {};
          const so = result.uc.serviceOrders.find(e => e.soResult && e.soResult.actionTypeResult && e.soResult.actionTypeResult.id === Number(x.id));
          const series = {
            type: 'scatter',
            name: cashCollectionActionTypesResults.some(e => e === x.key) && so ? so.soType.name + ' - ' + x.name : x.name,
            data: monthYearAxis.map((monthYear, i) => {
              const found = result.uc.serviceOrders.filter(y => (this.jsonDateToMonthYearFormatted(y.dateExecuted) === monthYear || (!this.jsonDateToMonthYearFormatted(y.dateExecuted) && this.jsonDateToMonthYearFormatted(y.dateCreated) === monthYear)) && objectPath.get(y, 'soResult.actionTypeResult.id') === Number(x.id));
              const soTypes = [];
              if (found.length > 0) {
                found.forEach(sot => {
                  const sotDate = objectPath.get(sot, 'dateExecuted') != null && objectPath.get(sot, 'dateExecuted') !== undefined ? objectPath.get(sot, 'dateExecuted') : objectPath.get(sot, 'dateCreated');
                  soTypes.push({
                    soType: objectPath.get(sot, 'soType.name'),
                    date: sotDate
                  });
                });
                objectPath.set(tooltipData, monthYear, { soTypes: soTypes, actionTypeResult: x.name });
                if (consumptionInvoice.find(z => z !== undefined && z !== null && z !== 0) === undefined) {
                  return 0.1;
                } else {
                  const pos = consumptionInvoice[i];
                  return pos <= 0 || Number.isNaN(pos) ? 0.1 : pos;
                }
              }
              return null;
            }),
            marker: {
              fillColor: x.fillColor,
              symbol: x.symbol
            },
            tooltipData: tooltipData,
            yAxis: chartOptions.yAxis.findIndex(a => a.referenceChannel)
          };
          if (series.data.filter(d => d).length > 0) {
            chartOptions.series.push(series);
          }
        });

        // Service Order Types markers
        soTypesMarkers.forEach(x => {
          const tooltipData = {};
          const series = {
            type: 'scatter',
            name: x.name,
            data: monthYearAxis.map((monthYear, i) => {
              const found = result.uc.serviceOrders.filter(y => (this.jsonDateToMonthYearFormatted(y.dateExecuted) === monthYear || (!this.jsonDateToMonthYearFormatted(y.dateExecuted) && this.jsonDateToMonthYearFormatted(y.dateCreated) === monthYear)) && objectPath.get(y, 'soType.id') === x.id && !objectPath.get(y, 'soResult.actionTypeResult'));
              const soTypes = [];
              if (found.length > 0) {
                found.forEach(sot => {
                  const sotDate = objectPath.get(sot, 'dateExecuted') != null && objectPath.get(sot, 'dateExecuted') !== undefined ? objectPath.get(sot, 'dateExecuted') : objectPath.get(sot, 'dateCreated');
                  soTypes.push({
                    soType: objectPath.get(sot, 'soType.name'),
                    date: sotDate,
                    soTechnician: objectPath.get(sot, 'soTechnician1.name'),
                    soResult: objectPath.get(sot, 'soResult.id') !== null && objectPath.get(sot, 'soResult.id') !== undefined ? `${objectPath.get(sot, 'soResult.id')} ${objectPath.get(sot, 'soResult.name')}` : null,
                    commentNote: objectPath.get(sot, 'commentNote')
                  });
                });
                objectPath.set(tooltipData, monthYear, { soTypes: soTypes });
                if (consumptionInvoice.find(z => z !== undefined && z !== null && z !== 0) === undefined) {
                  return 0.1;
                } else {
                  const pos = consumptionInvoice[i];
                  return pos <= 0 || Number.isNaN(pos) ? 0.1 : pos;
                }
              }
              return null;
            }),
            marker: {
              fillColor: x.fillColor,
              symbol: x.symbol
            },
            tooltipData: tooltipData,
            yAxis: chartOptions.yAxis.findIndex(a => a.referenceChannel)
          };
          if (series.data.filter(d => d).length > 0) {
            chartOptions.series.push(series);
          }
        });
      }

      // Meter Reader Note Groups markers
      const bmNotesMarkers = [];
      result.billingMeasurementNotes.forEach(bmn => {
        if (bmn.measurementNote && bmn.measurementNote.noteGroup && !bmNotesMarkers.some(e => e.id === bmn.measurementNote.noteGroup.id)) {
          const ng: any = bmn.measurementNote.noteGroup;

          if (ng.symbol || ng.fillColor) {
            bmNotesMarkers.push({
              id: ng.id,
              name: ng.name,
              key: ng.name,
              symbol: objectPath.get(ng, 'symbol', 'square'),
              fillColor: objectPath.get(ng, 'color', '#000000')
            });
          }
        }
      });

      bmNotesMarkers.forEach(x => {
        const tooltipData = {};
        const series = {
          type: 'scatter',
          name: x.name,
          data: monthYearAxis.map((monthYear, i) => {
            let found = [];
            if (result.billingMeasurementNotes && result.billingMeasurementNotes.length > 0) {
              found = found.concat(result.billingMeasurementNotes.filter(y => {
                return this.jsonDateToMonthYearFormatted(y.dateReference) === monthYear && objectPath.get(y, 'measurementNote.noteGroup.id') === x.id;
              }));
            }
            const measurementNotes = [];
            if (found.length > 0) {
              found.forEach(mn => {
                measurementNotes.push({
                  measurementNote: objectPath.get(mn, 'measurementNote.name'),
                  date: objectPath.get(mn, 'dateReference'),
                  commentNote: objectPath.get(mn, 'commentNote')
                });
              });
              objectPath.set(tooltipData, monthYear, { measurementNotes: measurementNotes, noteGroup: objectPath.get(found[0], 'measurementNote.noteGroup.name') });
              if (consumptionInvoice.find(z => z !== undefined && z !== null && z !== 0) === undefined) {
                return 0.1
              } else {
                const pos = consumptionInvoice[i];
                return pos <= 0 || Number.isNaN(pos) ? 0.1 : pos;
              }
            } else {
              return null;
            }
          }),
          marker: {
            fillColor: x.fillColor,
            symbol: x.symbol
          },
          tooltipData: tooltipData,
          yAxis: chartOptions.yAxis.findIndex(a => a.referenceChannel)
        }
        if (series.data.filter(d => d).length > 0) {
          chartOptions.series.push(series);
        }
      });

      const self = this;
      chartOptions.tooltip.formatter = function () {
        return self.getTooltip(this);
      }

      if (chartOptions && chartOptions.series && chartOptions.series.length) {
        chartOptions.series.forEach(data => {
          if (data && data.marker) {
            data.marker.lineColor = data.marker.fillColor ? data.marker.fillColor : '#000';
            data.marker.radius = 4;
            switch ((data.marker.symbol)) {
              case 'x':
                data.marker.lineWidth = 2;
                data.marker.radius = 8;
                break;
              case 'cross':
                data.marker.lineWidth = 3;
                data.marker.radius = 8;
                break;
              case 'bar':
                data.marker.lineWidth = 4;
                data.marker.radius = 9;
                break;
              case 'star':
                data.marker.lineWidth = 4;
                data.marker.radius = 9;
                break;
              case 'house':
                data.marker.lineWidth = 2;
                data.marker.radius = 8;
                break;
              case 'verticalBar':
                data.marker.lineWidth = 7;
                data.marker.radius = 8;
                break;
              case 'emptySquare':
                data.marker.lineColor = data.marker.fillColor ? data.marker.fillColor : '#000';
                data.marker.fillColor = 'transparent'
                data.marker.lineWidth = 4;
                data.marker.radius = 8;
                break;
              case 'emptyCross':
                data.marker.lineColor = data.marker.fillColor ? data.marker.fillColor : '#000';
                data.marker.fillColor = 'transparent'
                data.marker.lineWidth = 4;
                data.marker.radius = 8;
                break;
              case 'emptyCircle':
                data.marker.symbol = 'circle'
                data.marker.lineColor = data.marker.fillColor ? data.marker.fillColor : '#000';
                data.marker.fillColor = 'transparent'
                data.marker.lineWidth = 4;
                data.marker.radius = 8;
                break;
              case 'circle':
                data.marker.radius = data.marker.fillColor ? 8 : 4;
                break;
              case 'triangle':
              case 'triangle-down':
              case 'diamond':
              case 'square':
                data.marker.radius = 8;
                break;
            }
          }
        });
      }

      result.chartOptions = chartOptions;

      ucDetails.next(result);
    },
      (err) => {
        this.translate.get('ERROR_CONSUMPTION_DATA').subscribe((text: string) => {
          this.notificationService.showNotification(text);
        });
      });

    return ucDetails.asObservable();
  }

  rgbToHex(chartMarkerColor: string) {
    if (chartMarkerColor != null) {
      const r = Number(chartMarkerColor.split(',')[0]);
      const b = Number(chartMarkerColor.split(',')[2]);
      const g = Number(chartMarkerColor.split(',')[1]);
      const rgb = b | (g << 8) | (r << 16);

      return '#' + rgb.toString(16);
    } else {
      return '#000000';
    }
  }

  setIconPositionBelow(offset: number, eventPoint:  Highcharts.PointObject, lastIconPositions: any) {
    const pointNewPosition = lastIconPositions.lastIconBelowYPosition ? lastIconPositions.lastIconBelowYPosition - offset : eventPoint.y - offset;
    if (pointNewPosition > 0) {
      lastIconPositions.lastIconBelowYPosition = pointNewPosition;
      eventPoint.y = pointNewPosition;
    } else {
      this.setIconPositionAbove(offset, eventPoint, lastIconPositions);
    }
  }

  setIconPositionAbove(offset: number, eventPoint:  Highcharts.PointObject, lastIconPositions: any) {
    const pointNewPosition = lastIconPositions.lastIconAboveYPosition ? lastIconPositions.lastIconAboveYPosition + offset : eventPoint.y + offset;
    lastIconPositions.lastIconAboveYPosition = pointNewPosition;
    eventPoint.y = pointNewPosition;
  }

  rearrangeEventsIcons(chart_: Highcharts.ChartObject) {
    const extremes = chart_.yAxis[0].getExtremes();
    let axisMaxValue = extremes.max;
    const axisMinValue = extremes.min;
    const lines = chart_.options.yAxis[0].tickAmount;
    const offset = (axisMaxValue - axisMinValue) / lines;
    const referenceSerie: any = chart_.series.filter(s => (s as any).userOptions.referenceChannel);
    const scatterSeries = chart_.series.filter(s => s.type === 'scatter');
    if (referenceSerie && scatterSeries != null && scatterSeries.length > 0) {
      for (let x = 0; x < scatterSeries[0].data.length; x++) {
        const lastIconPositions = {
          lastIconAboveYPosition: undefined,
          lastIconBelowYPosition: undefined
        };
        for (let i = 0; i < scatterSeries.length; i++) {
          if (scatterSeries[i].data[x].y) {
            if (i % 2 === 0) {
              this.setIconPositionBelow(offset, scatterSeries[i].data[x], lastIconPositions);
            } else {
              this.setIconPositionAbove(offset, scatterSeries[i].data[x], lastIconPositions);
              if (lastIconPositions.lastIconAboveYPosition > axisMaxValue) {
                axisMaxValue += offset;
              }
            }
          }
        }
      }
    }
    chart_.yAxis[0].setExtremes(axisMinValue, axisMaxValue);
    chart_.reflow();
  }

  getTooltip(chartOptions) {
    let tooltip = `<span style="font-size:10px;font-style: italic;">${chartOptions.x}</span><br>`;

    if (objectPath.get(chartOptions, `series.userOptions.tooltipData.${chartOptions.x}`)) {

      let soTypes, actionTypeResult, noteGroup, measurementNotes;
      const hoverSoType = objectPath.get(chartOptions, `series.userOptions.tooltipData.${chartOptions.x}.soTypes`, null);

      if (soTypes = objectPath.get(chartOptions, `series.userOptions.tooltipData.${chartOptions.x}.soTypes`, null)) {
        soTypes.forEach(sot => {
          tooltip += `${this.translations['SERVICE_ORDER_TYPE']} (${moment(sot.date).format('DD/MM/YYYY')}): <strong>${sot.soType}`;
          tooltip += sot.soResult !== null && sot.soResult !== undefined ? ` - ${sot.soResult} </strong><br>` : `</strong><br>`;
          if (actionTypeResult = objectPath.get(chartOptions, `series.userOptions.tooltipData.${chartOptions.x}.actionTypeResult`, null)) {
            tooltip += `${this.translations['ACTION_TYPE_RESULT']}: <strong>${actionTypeResult}</strong><br>`;
          }
          if (sot.soTechnician) {
            tooltip += `${this.translations['SO_TECHNICIAN_1']}: <strong>${sot.soTechnician}</strong><br>`;
          }
          if (sot.commentNote) {
            tooltip += `${this.translations['NOTE']}: <strong>${sot.commentNote}</strong><br>`;
          }
        });
      }

      if (noteGroup = objectPath.get(chartOptions, `series.userOptions.tooltipData.${chartOptions.x}.noteGroup`, null)) {
        tooltip += `${this.translations['NOTE_GROUP']}: <strong>${noteGroup}</strong><br>`;
      }

      if (measurementNotes = objectPath.get(chartOptions, `series.userOptions.tooltipData.${chartOptions.x}.measurementNotes`, null)) {
        measurementNotes.forEach(mn => {
          tooltip += `${this.translations['MEASUREMENT_NOTE']} (${moment(mn.date).format('DD/MM/YYYY')}): <strong>${mn.measurementNote}</strong>`;
          tooltip += mn.commentNote ? ` - <strong>${mn.commentNote}</strong><br>` : `<br>`;
        });
      }

      objectPath.get(chartOptions, `series.chart.series`, null).forEach(element => {
        /*if ((soTypes.filter(sort => sort.soType === element.name).length === 0) && element.name !== actionTypeResult && element.name !== noteGroup && element.name !== measurementNotes) {
          if (element.visible) {
            const consumption = element.yData[chartOptions.point.index];
              tooltip += `${element.name}: <strong>${Math.floor(consumption)} </strong><br>`;
            }
          }*/
        if (element.userOptions.type === 'line') {
          if (element.visible) {
            const consumption = element.yData[chartOptions.point.index];
            tooltip += `${element.name}: <strong>${Math.floor(consumption)}</strong><br>`;
          }
        }
      });

    } else {
      const pointIndex = chartOptions.series.points.indexOf(chartOptions.point);
      const consumption = chartOptions.series.chart.series[chartOptions.series.index].yData[pointIndex];
      const channelUnit = chartOptions.series.chart.series[chartOptions.series.index].tooltipOptions.valuePrefix;
      tooltip += `${chartOptions.series.chart.series[chartOptions.series.index].name}: <strong>${channelUnit.indexOf('$') === -1 ? Math.floor(consumption) : consumption}</strong><br>`;
    }

    return tooltip;
  }

  exportUcs(ids: string[], fields?: string, configSection?: string, filter?: string): Observable<any> {
    if (filter != null) {
      filter += `AND Id IN(`
      filter += ids.map(id => `'${id}'`).join(',');
      filter += `)`;
    } else {
      filter = `Id IN(`
      filter += ids.map(id => `'${id}'`).join(',');
      filter += `)`;
    }

    const transport = popsicle.createTransport({ type: 'blob' });
    const options = {
      user: {
        sign: (x) => {
          x.transport = transport;
          return x;
        }
      }
    };

    const ucSearchPost: UcSearchPost.UcSearchPost = {
      'fields': fields,
      'filter': filter,
      'patternsFilter': null,
      'selectionsFilter': null,
      'group': null,
      'AggregatedFields': null,
      'sort': 'Id',
      'page': null,
      'skip': null,
      'take': null
    };

    return this.apiService.client.ucs.export.POST(ucSearchPost, configSection, options);
  }

  update(ep: Ucpatch.Ucpatch): Observable<any[]> {
    const headers = new Headers({ 'Content-Type': 'application/json' });
    this.options = new RequestOptions({ headers: headers });
    const ucPatch = { 'idUCClassification': ep.ucClassification.id };
    return this.apiService.client.ucs.ucId(ep.id.toString()).PATCH(ucPatch, this.options)
      .map((res) => {
        return this.apiService.handleResponse(res);
      });
  }

  getNeighboring(endpoint: MeterNeighborDto, neighborsQty: number, neighborsMeterReadingDateColumn: string, neighborsMeterItineraryColumn: string, openDebt: string): Observable<any> {
    const hasMeterReadingDate = endpoint !== null && moment(endpoint.meterReadingDatetime, this.languageService.actualDateFormat).isValid();
    if (hasMeterReadingDate) {
      const apiQueryParamsBefore: ApiQueryParams = {
        fields: `id, address, ${neighborsMeterReadingDateColumn}, referenceConsumption,${openDebt}`,
        sort: `-${neighborsMeterReadingDateColumn}`,
        skip: 0,
        take: neighborsQty
      };
      apiQueryParamsBefore.filter = `id ne \"${endpoint.id}\"
        and ${neighborsMeterReadingDateColumn} le \"${moment(endpoint.meterReadingDatetime, this.languageService.actualDateFormat).format('YYYY-MM-DDTHH:mm:ss.SSS')}\"
        and ${neighborsMeterItineraryColumn} eq \"${endpoint.meterReadingItinerary}\"
      `;

      const apiQueryParamsAfter: ApiQueryParams = { ...apiQueryParamsBefore };
      apiQueryParamsAfter.sort = `${neighborsMeterReadingDateColumn}`;
      apiQueryParamsAfter.filter = apiQueryParamsBefore.filter.replace('le', 'ge');

      const $metersBeforeObservable = this.getUCs(apiQueryParamsBefore);
      const $metersAfterObservable = this.getUCs(apiQueryParamsAfter)
      return $metersBeforeObservable.combineLatest($metersAfterObservable);

    } else {
      return of([]);
    }
  }

  getIsBlocked(ucId, actionTypeId): Observable<any> {
    const today = new Date();
    const paramsUcSearch: ApiQueryParams = {
      fields: `id`,
      filter: `id eq \"${ucId}\" and UCBlockSnrs.Any(ActionTypeId eq \"${actionTypeId}\" and expiresAt gt \"${today.toISOString()}\")`
    };
    return this.getAll(paramsUcSearch);
  }

  GetExportCsv(filter: any, fields: string): Observable<any> {

    const ucSearchPost: UcSearchPost.UcSearchPost = {
      'fields': fields,
      'filter': filter,
      'patternsFilter': null,
      'selectionsFilter': null,
      'group': null,
      'AggregatedFields': null,
      'sort': 'Id',
      'page': null,
      'skip': null,
      'take': null
    };

    return this.apiService.client.ucs.generatecsv.POST(ucSearchPost).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  getExportedFileDownload(filename: string): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Bearer ' + this.oAuth.getAccessToken());
    const url = `${ConfigService.app.baseUri}/ucs/downloadcsv?fileName=` + filename;
    return this.http.get(url, { headers: headers, responseType: 'blob' });
  }

  getExportedFiles(filter: string): Observable<any> {
    return this.apiService.client.ucs.csvlist.GET(filter).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  getColumnMappingDetail(): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Bearer ' + this.oAuth.getAccessToken()).set('Content-Type', 'application/json; charset=utf-8');
    const url = `${ConfigService.app.baseUri}/ucs/getcolumndetail`;
    return this.http.get(url, { headers: headers });
  }

  importUcs(ucsList): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Bearer ' + this.oAuth.getAccessToken()).set('Content-Type', 'application/json; charset=utf-8');
    const url = `${ConfigService.app.baseUri}/ucs/ucsvalidate`;
    return this.http.post(url, ucsList, { headers: headers });
  }

  addSelectedColumn(context: string, columns: string): Observable<any> {
    return this.apiService.client.ucs.addselectedcolumns.POST(context, columns).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }

  getSelectedColumn(context: string): Observable<any> {
    return this.apiService.client.ucs.getselectedcolumns.GET(context).map((res) => {
      return this.apiService.handleResponse(res);
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/core/api/uc.service.ts